import React, { useMemo } from "react";
import styled, { css } from "styled-components";
import moment from "moment-timezone";
import { useHistory } from "react-router-dom";

import { Text } from "src/library/components/Text";
import {
  IOrder,
  OrderOrigin
} from "src/core/orders/api/orders.api";
import { Button } from "src/library/components/Button";
import { calculateCartTotalQuantity } from "src/core/orders/utils/orders";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";
import useCanAmendOrder from "src/core/orders/hooks/validation/useCanAmendOrder";
import { useOrder } from "src/core/orders/hooks/index";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import { TotalPrice } from "src/core/prices/components/TotalPrice";

export interface OrderDayPreviewCardProps {
  order: IOrder | null;
  date: Date;
  unavailableOrderDay?: boolean;
}

const OrderDayPreviewCard: React.ReactFCWithChildren<OrderDayPreviewCardProps> = ({
  order, date, unavailableOrderDay
}) => {
  const canBeAmended = useCanAmendOrder();
  const checkUserPermission = useCheckUserPermission();
  const history = useHistory();
  const [ { isCreatingOrder }, { createOrder } ] = useOrder();

  const isToday = useMemo(() => {
    return moment.utc().format("YYYY-MM-DD") === moment.utc(date).format("YYYY-MM-DD");
  }, [ date ]);

  // decides and returns the relevant card action
  const cardAction = useMemo(() => {
    if (order) {
      const canBeAmendOrder = canBeAmended(order);

      // "Edit order" if can amend order
      if (checkUserPermission("amend-order")) {
        if (canBeAmendOrder === null) {
          return (
            <Button
              $buttonType="plain"
              onClick={() => history.push(`/orders/${order.id}`, { edit: true })}
            >
              Edit Order
            </Button>
          );
        }
      }

      // "View order" if order exists
      return (
        <Button
          $buttonType="plain"
          onClick={() => history.push(`/orders/${order.id}`)}
        >
          View Order
        </Button>
      );
    }

    // "Add products" to create an order on this date. Disabled if unavailable (ie past cutoff)
    return (
      <Button
        $buttonType="plain"
        disabled={unavailableOrderDay}
        onClick={() => {
          if (!unavailableOrderDay) {
            createOrder(date, OrderOrigin.Manual);
          }
        }}
      >
        {unavailableOrderDay ? "-" : "Add Products"}
      </Button>
    );
  }, [
    canBeAmended,
    checkUserPermission,
    history,
    order,
    unavailableOrderDay
  ]);

  return (
    <PreviewCardWrapper>
      <div className="card-header">
        <Text weight={order?.orderLines ? "bold" : "normal"}>
          {isToday ? "Today" : moment.utc(date).format("dddd")}
        </Text>

        <Text>
          {moment.utc(date).format("DD MMM")}
        </Text>
      </div>

      <div className="quantity-total">
        {order?.orderLines ?
          (
            <Text>
              <span className="quantity-value">
                {calculateCartTotalQuantity(order.orderLines)}

                {" "}
              </span>
              items

              {" "}

              <span className="quantity-value total">
                <TotalPrice
                  lines={order.orderLines}
                  id={order.id}
                />

                {" "}
              </span>
              total
            </Text>
          ) :
          (
            <Text>
              No order today
            </Text>
          )}
      </div>

      <div className="action-option">
        {isCreatingOrder ? (
          <LoadingSpinner
            type="flat"
            position="inline"
          />
        ) : cardAction}
      </div>
    </PreviewCardWrapper>
  );
};

const PreviewCardWrapper = styled.div`
  ${({ theme }) => css`
      width: 100%;
      border-radius: ${theme.borderRadius};
      background: ${theme.colors.white};
      margin-bottom: ${theme.spacing.margins.md};

      .card-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-bottom: 1px solid ${theme.colors.lightGrey};
        padding: ${theme.spacing.margins.sm};
      }

      .order-lines {
        height: 116px;
        overflow: hidden;

        &.no-order {
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .first-order-line-row {
          border-bottom: 1px solid ${theme.colors.lightGrey};
        }
      }

      .quantity-total {
        width: 100%;
        height: 35px;
        display: flex;
        justify-content: center;
        align-items: center;

        .quantity-value {
          font-weight: bold;
          
          .current-price {
            font-weight: bold;
          }

          &.total {
            margin-left: ${theme.spacing.margins.sm};
          }
        }
      }

      .action-option {
        border-top: 1px solid ${theme.colors.lightGrey};
        width: 100%;
        height: 35px;
        display: flex;
        justify-content: center;
        align-items: center;
        
        > div button {
          margin: 0 auto;
          font-weight: bold;

          &[disabled] {
            background: none;
          }
        }
      }
  `}
`;

export default OrderDayPreviewCard;
